import React, { useEffect } from 'react';

import type { CategoryDataItem, DeliverableDataItem, ProductDataItem } from 'pages/access_codes/components/AssignContent/AssignContent';
import { DeliverableContentTreeView } from 'components/DeliverableContentTreeView/DeliverableContentTreeView';
import { ContentDeliverable, CourseGating, TransactionApi, TransactionDetails } from 'apis/TransactionApi';
import { GMetrixCheckInput } from 'components/GMetrixCheckInput/GMetrixCheckInput';
import { GMetrixDatePicker } from 'components/GMetrixDatePicker/GMetrixDatePicker';
import { GMetrixDropdown } from 'components/GMetrixDropdown/GMetrixDropdown';
import { GroupDetailsContext, useGroupDetails } from 'pages/groups/context';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixDialog } from 'components/GMetrixDialog/GMetrixDialog';
import { GMetrixInput } from 'components/GMetrixInput/GMetrixInput';
import { TransactionPermissions } from 'pages/access_codes/types';
import { FindContent } from 'components/FindContent/FindContent';
import { Alert, Col, Form, Row } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { GroupPermissions } from 'pages/groups/types';
import { useNotification } from 'app/notifications';
import { compareDays, formatDay } from 'utils';
import { useSuperState } from 'hooks';

interface ClassroomContentProps {
    contentTiedToInactiveOrder: boolean;
    permissions: GroupPermissions | TransactionPermissions;
}

interface DefaultSettingsForm {
    booksPerStudent: number;
    courseGateType: any;
    coursesPerCode: number;
    enableBulb: boolean;
    enableCloud: boolean;
    enableQuestionReview: boolean;
    enableStudyGuide: boolean;
    endDate: Date | null;
    startDate: Date | undefined;
    testMode: boolean;
    testsPerStudent: number;
    trainingMode: boolean;
}

const treeViewDataManipulation = (content: void | ContentDeliverable[]): Promise<CategoryDataItem[]> =>

    // This is messy I'm sorry, just tryna get a treeview to work.
    // eslint-disable-next-line implicit-arrow-linebreak
    new Promise((resolve, reject) => {

        if (content && content.length > 0) {

            const treeviewData: any[] = [];
            const initiallyExpanded = content.length <= 500;
            for (let i = 0; i < content.length; i++) {

                const deliverableItem: ContentDeliverable = content[i];
                const categoryIndex = treeviewData.findIndex(
                    (item: any) => item.categoryId === deliverableItem.CategoryId || item.text === deliverableItem.CategoryName
                );
                const productIndex = treeviewData[categoryIndex]?.items.findIndex(
                    (item: any) => item.productId === deliverableItem.ProductId
                );
                if (categoryIndex === -1) {

                    treeviewData.push({
                        categoryId: deliverableItem.CategoryId,
                        deliverableTypeId: deliverableItem.DeliverableTypeId,
                        expanded: initiallyExpanded,
                        items: [
                            {
                                deliverableTypeId: deliverableItem.DeliverableTypeId,
                                expanded: initiallyExpanded,
                                items: [
                                    {
                                        ...deliverableItem,
                                        expanded: initiallyExpanded,
                                        key: `${deliverableItem.Id}-${deliverableItem.DeliverableTypeId}`,
                                        text: deliverableItem.Name
                                    }
                                ],
                                key: `${deliverableItem.ProductId}-${deliverableItem.DeliverableTypeId}`,
                                productId: deliverableItem.ProductId,
                                text: deliverableItem.ProductName
                            }
                        ],
                        key: `${deliverableItem.CategoryId}-${deliverableItem.DeliverableTypeId}`,
                        text: deliverableItem.CategoryName
                    });

                }

                if (productIndex === -1) {

                    treeviewData[categoryIndex].items.push({
                        deliverableTypeId: deliverableItem.DeliverableTypeId,
                        expanded: initiallyExpanded,
                        items: [
                            {
                                ...deliverableItem,
                                expanded: initiallyExpanded,
                                key: `${deliverableItem.Id}-${deliverableItem.DeliverableTypeId}`,
                                text: deliverableItem.Name
                            }
                        ],
                        key: `${deliverableItem.ProductId}-${deliverableItem.DeliverableTypeId}`,
                        productId: deliverableItem.ProductId,
                        text: deliverableItem.ProductName
                    });

                } else if (treeviewData.length > 0) {

                    treeviewData[categoryIndex]?.items[productIndex].items.push({
                        ...deliverableItem,
                        expanded: initiallyExpanded,
                        key: `${deliverableItem.Id}-${deliverableItem.DeliverableTypeId}`,
                        text: deliverableItem.Name
                    });

                }

            }
            resolve(treeviewData);

        } else {

            resolve([]);

        }

    });

export function ClassroomContent({
    contentTiedToInactiveOrder,
    permissions
}: ClassroomContentProps) {

    const [state, setState] = useSuperState({
        courseGatingTypes: [] as CourseGating[],
        defaultSettingsForm: {
            booksPerStudent: 0,
            courseGateType: undefined,
            coursesPerCode: 0,
            enableBulb: false,
            enableCloud: false,
            enableQuestionReview: false,
            enableStudyGuide: false,
            endDate: null,
            startDate: undefined,
            testMode: false,
            testsPerStudent: 0,
            trainingMode: false
        } as DefaultSettingsForm,
        error: null as string,
        loading: false,
        search: '',
        showContentModal: false,
        showDefaultEditModal: false,
        treeViewData: [] as CategoryDataItem[]
    });
    const { courseGatingTypes, defaultSettingsForm, error, loading, search, showContentModal, showDefaultEditModal, treeViewData } = state;

    const { pageContext, updateById } = useGroupDetails();
    const intl = useIntl();
    const { formatMessage } = intl;
    const { createToast } = useNotification();

    const clearError = () => setState({ error: null });

    const setDefaultSettingsForm = (defaultSettingsForm: DefaultSettingsForm) => setState({ defaultSettingsForm });

    const parseNumberValue = (value: string, fallback: number, max: number) => {

        const parsedNumber = parseInt(value, 10);
        if (Number.isNaN(parsedNumber)) {

            return fallback;

        }

        if (parsedNumber < 0) {

            return 0;

        }

        if (parsedNumber > max) {

            return max;

        }

        return parsedNumber;

    };

    const getCourseGatingTypes = async () => {

        try {

            const courseGatingTypes = await TransactionApi.GetCourseGatingTypes(1);
            setState({ courseGatingTypes });

        } catch (e) {

            console.error(e);

        }

    };

    const hierarchyFilter = (data: any): CategoryDataItem[] => {

        const hierarchySearchedData: CategoryDataItem[] = [];
        if (data.length === 0) return [];

        data.map((categoryItem: CategoryDataItem) => categoryItem.items.map((productItem: ProductDataItem) => productItem.items.map((dataItem: DeliverableDataItem) => {

            if (dataItem.text && dataItem.text.toLowerCase().includes(search.toLowerCase())) {

                let categoryIndex = hierarchySearchedData.findIndex(
                    (category: CategoryDataItem) => category.categoryId === categoryItem.categoryId
                );
                let productIndex = -1;
                if (categoryIndex === -1) {

                    hierarchySearchedData.push({ ...categoryItem, items: [] });
                    categoryIndex = hierarchySearchedData.findIndex(
                        (category: CategoryDataItem) => category.categoryId === categoryItem.categoryId
                    );

                } else {

                    productIndex = hierarchySearchedData[categoryIndex].items.findIndex(
                        (product: ProductDataItem) => product.productId === productItem.productId
                    );

                }
                if (productIndex === -1) {

                    hierarchySearchedData[categoryIndex].items.push({ ...productItem, items: [] });
                    productIndex = hierarchySearchedData[categoryIndex].items.findIndex(
                        (product: ProductDataItem) => product.productId === productItem.productId
                    );

                }
                {

                    hierarchySearchedData[categoryIndex].items[productIndex].items.push(dataItem);

                }

            }

        })));

        return hierarchySearchedData;

    };

    const handleSaveDefaultSettings = async () => {

        try {

            setState({ loading: true });

            //validation should go here
            const result = await TransactionApi.Update({
                ...pageContext?.Data.transactionDetails,
                AllowReview: defaultSettingsForm.enableQuestionReview,
                AllowStudyGuide: defaultSettingsForm.enableStudyGuide,
                CourseGateType: defaultSettingsForm.courseGateType,
                ExpirationDate: defaultSettingsForm.endDate,
                HasBulbAccess: defaultSettingsForm.enableBulb,
                HasCloud: defaultSettingsForm.enableCloud,
                NumOfBooks: defaultSettingsForm.booksPerStudent,
                NumOfCourses: defaultSettingsForm.coursesPerCode,
                NumOfTests: defaultSettingsForm.testsPerStudent,
                StartDate: defaultSettingsForm.startDate,
                TestingMode: defaultSettingsForm.testMode,
                TrainingMode: defaultSettingsForm.trainingMode
            } as TransactionDetails);

            if (!result?.Done) throw new Error(result?.Message);

            // Keep each student's per-record expiration in sync with the default when it changes,
            // so the edit dialogs on the student pages reflect the new end date.
            const previousEndDate = pageContext?.Data.transactionDetails?.ExpirationDate ?? null;
            const newEndDate = defaultSettingsForm.endDate ?? null;
            const endDateChanged = !!previousEndDate !== !!newEndDate
                || (!!previousEndDate && !!newEndDate && compareDays(previousEndDate, newEndDate) !== 0);
            if (endDateChanged && newEndDate) {

                await TransactionApi.UpdateStudentExpirationByTransaction(pageContext?.Data.transactionDetails?.Id ?? 0, newEndDate);

            }

            createToast(formatMessage({ id: 'the_content_was_saved_successfully.6c899203', defaultMessage: 'The content was saved successfully' }), 'success');

            updateById(pageContext?.Id ?? 0, {
                transactionDetails: {
                    ...pageContext?.Data.transactionDetails,
                    AllowReview: defaultSettingsForm.enableQuestionReview,
                    AllowStudyGuide: defaultSettingsForm.enableStudyGuide,
                    CourseGateType: defaultSettingsForm.courseGateType,
                    ExpirationDate: defaultSettingsForm.endDate,
                    HasBulbAccess: defaultSettingsForm.enableBulb,
                    HasCloud: defaultSettingsForm.enableCloud,
                    NumOfBooks: defaultSettingsForm.booksPerStudent,
                    NumOfCourses: defaultSettingsForm.coursesPerCode,
                    NumOfTests: defaultSettingsForm.testsPerStudent,
                    StartDate: defaultSettingsForm.startDate,
                    TestingMode: defaultSettingsForm.testMode,
                    TrainingMode: defaultSettingsForm.trainingMode
                } as TransactionDetails
            });

            setState({
                loading: false,
                showDefaultEditModal: false
            });

        } catch (e) {

            setState({
                error: e?.message || e,
                loading: false
            });

        }

    };

    useEffect(() => {

        if (pageContext?.Data.transactionDetails) {

            const defaultSettings = pageContext?.Data.transactionDetails;
            setDefaultSettingsForm({
                booksPerStudent: defaultSettings.NumOfBooks ?? 0,
                courseGateType: defaultSettings.CourseGateType ?? 0,
                coursesPerCode: defaultSettings.NumOfCourses ?? 0,
                enableBulb: defaultSettings.HasBulbAccess ?? false,
                enableCloud: defaultSettings.HasCloud ?? false,
                enableQuestionReview: defaultSettings.AllowReview ?? false,
                enableStudyGuide: defaultSettings.AllowStudyGuide ?? false,
                endDate: defaultSettings.ExpirationDate,
                startDate: defaultSettings.StartDate,
                testMode: defaultSettings.TestingMode,
                testsPerStudent: defaultSettings.NumOfTests ?? 0,
                trainingMode: defaultSettings.TrainingMode
            });

        }

    }, [pageContext?.Data.transactionDetails, showDefaultEditModal]);

    useEffect(() => {

        getCourseGatingTypes();

    }, []);

    useEffect(() => {

        (async () => {

            if (pageContext && pageContext.Data.content) {

                const treeViewData = await treeViewDataManipulation(pageContext?.Data.content);
                setState({ treeViewData });

            } else {

                setState({ treeViewData: [] });

            }

        })();

    }, [pageContext?.Data.content]);

    return (
        <>
            <Alert dismissible onClose={clearError} show={!!error} variant={'danger'}>
                {error}
            </Alert>

            <Row className="mb-3 gap-2 gap-md-0">
                <Col md={4} xs={12}>
                    <GMetrixInput
                        onChange={e => setState({ search: e.target.value })}
                        placeholder={formatMessage({ id: 'search_courses_tests_or_assignments.b8c4464f', defaultMessage: 'Search courses, tests, or assignments...' })}
                        type="text"
                        value={search}
                    />
                </Col>
                <Col className="d-flex justify-content-md-end" md={8} xs={12}>
                    <GMetrixButton
                        className="me-2"
                        iconClass="fa-regular fa-cog"
                        onClick={() => setState({ showDefaultEditModal: true })}
                        variant="outline-primary"
                    >
                        <FormattedMessage defaultMessage="Default Settings" id="default_settings.e49d564c" />
                    </GMetrixButton>
                    <GMetrixButton
                        disabled={pageContext?.Data.secondaryLoading}
                        iconClass={pageContext?.Data.secondaryLoading ? 'fa-regular fa-spinner fa-spin' : 'fa-regular fa-plus'}
                        onClick={() => setState({ showContentModal: true })}
                        variant="primary"
                    >
                        <FormattedMessage defaultMessage="Add Content" id="add_content.aad4180d" />
                    </GMetrixButton>
                </Col>
            </Row>

            <hr />

            {treeViewData.length > 0 && pageContext?.Data.content ? (
                <DeliverableContentTreeView
                    courseGatingTypes={courseGatingTypes}
                    currentContext={GroupDetailsContext}
                    data={hierarchyFilter(treeViewData) ?? []}
                    totalListItemCount={pageContext.Data.content.length}
                />
            )
                : (
                    <div className="py-3 text-center content-settings__no-resources">
                        <FormattedMessage defaultMessage="No content has been added." id="no_content_has_been_added.9e012e26" />
                    </div>
                )}
            <GMetrixDialog
                actionButtonClick={handleSaveDefaultSettings}
                actionButtonDisabled={false}
                actionButtonText={formatMessage({ id: 'save.d44082ae', defaultMessage: 'Save' })}
                dialogLabel={formatMessage({ id: 'edit_default_settings.cf08e662', defaultMessage: 'Edit Default Settings' })}
                disabled={loading}
                onCancel={() => setState({ loading: false, showDefaultEditModal: false })}
                show={showDefaultEditModal}
                size="xl"
            >
                <Form>
                    {/* Default Settings */}
                    <h5 className="mb-3">
                        <FormattedMessage defaultMessage="Default Settings" id="default_settings.e49d564c" />
                    </h5>
                    <Row className="mb-3">
                        <Col md={6}>
                            <GMetrixDatePicker
                                onChange={date => {

                                    if (date === undefined) {

                                        setDefaultSettingsForm({ ...defaultSettingsForm, startDate: undefined });
                                        return;

                                    }
                                    setDefaultSettingsForm({ ...defaultSettingsForm, startDate: date as unknown as Date });

                                }}
                                infoTitle={formatMessage({ id: 'sets_the_start_date_of_the_content.f79467c1', defaultMessage: 'Sets the start date of the content' })}
                                label={formatMessage({ id: 'start_date.66ba5b72', defaultMessage: 'Start Date' })}
                                readOnly={loading}
                                value={defaultSettingsForm.startDate}
                            />
                        </Col>
                        <Col md={6}>
                            <GMetrixDatePicker
                                onChange={date => {

                                    if (date === undefined) {

                                        setDefaultSettingsForm({ ...defaultSettingsForm, endDate: null });
                                        return;

                                    }
                                    const endDate = date as unknown as Date;
                                    if (defaultSettingsForm.startDate && compareDays(defaultSettingsForm.startDate, endDate) > 0) {

                                        createToast(formatMessage({ id: 'you_cannot_set_the_end_date_before_the_s.e431eb11', defaultMessage: 'You cannot set the end date before the start date.' }), 'danger');
                                        return;

                                    }
                                    const licenseExpireDateStr = pageContext?.Data.mostRestrictiveLicense?.license.OrderExpireDate as unknown as string | undefined;

                                    // Day-precision compare: the license expiration day itself is allowed.
                                    if (licenseExpireDateStr && compareDays(endDate, licenseExpireDateStr) > 0) {

                                        createToast(formatMessage({ id: 'you_cannot_set_the_end_date_after_your_l.7bbc8d74', defaultMessage: 'You cannot set the end date after your license expiration date of {date}.' }, { date: formatDay(licenseExpireDateStr, intl) }), 'danger');
                                        return;

                                    }
                                    setDefaultSettingsForm({ ...defaultSettingsForm, endDate });

                                }}
                                infoTitle={formatMessage({ id: 'sets_the_end_date_of_the_content.542963b3', defaultMessage: 'Sets the end date of the content' })}
                                label={formatMessage({ id: 'end_date.10ec21c1', defaultMessage: 'End Date' })}
                                readOnly={loading}
                                value={defaultSettingsForm.endDate}
                            />
                        </Col>
                    </Row>
                    <div className="d-flex">
                        <GMetrixCheckInput
                            disabled
                            checked={defaultSettingsForm.enableBulb}
                            infoTitle={formatMessage({ id: 'enables_bulb_access.67a4b911', defaultMessage: 'Enables bulb access' })}
                            label={formatMessage({ id: 'enable_bulb_portfolio.a0c8d39b', defaultMessage: 'Enable bulb Portfolio' })}
                            onChange={e => setDefaultSettingsForm({ ...defaultSettingsForm, enableBulb: e.target.checked })}
                            type="checkbox"
                        />
                        {pageContext?.Data.licenses && pageContext.Data.licenses.some(license => license.CloudAccessUses > 0) && (
                            <GMetrixCheckInput
                                checked={defaultSettingsForm.enableCloud}
                                disabled={loading}
                                infoTitle={formatMessage({ id: 'enables_cloud_access.1d170166', defaultMessage: 'Enables cloud access' })}
                                label={formatMessage({ id: 'cloud_access.95fe7c27', defaultMessage: 'Cloud Access' })}
                                onChange={e => setDefaultSettingsForm({ ...defaultSettingsForm, enableCloud: e.target.checked })}
                                type="checkbox"
                            />
                        )}
                    </div>

                    <hr />

                    {/* Test Settings */}
                    <h5 className="mb-3">
                        <FormattedMessage defaultMessage="Test Settings" id="test_settings.dfedf589" />
                    </h5>
                    <Row className="mb-3">
                        <Col md={6}>
                            <GMetrixInput
                                disabled={loading}
                                infoTitle={formatMessage({ id: 'limit_the_number_of_test_attempts_that_a.5d029c56', defaultMessage: 'Limit the number of Test Attempts that a student can take across all Tests in this Classroom.' })}
                                label={formatMessage({ id: 'tests_per_student.44f8c927', defaultMessage: 'Tests per Student' })}
                                max={100}
                                onChange={e => setDefaultSettingsForm({ ...defaultSettingsForm, testsPerStudent: parseNumberValue(e.target.value, defaultSettingsForm.testsPerStudent, 100) })}
                                subLabel={`(max ${100})`}
                                type="number"
                                value={defaultSettingsForm.testsPerStudent}
                            />
                        </Col>
                        <Col md={6}>
                            <GMetrixDropdown
                                onChange={mode => {

                                    switch (mode.name) {

                                        case 'Training':
                                            setDefaultSettingsForm({ ...defaultSettingsForm, testMode: false, trainingMode: true });
                                            break;
                                        case 'Testing':
                                            setDefaultSettingsForm({ ...defaultSettingsForm, testMode: true, trainingMode: false });
                                            break;
                                        case 'Both':
                                        default:
                                            setDefaultSettingsForm({ ...defaultSettingsForm, testMode: true, trainingMode: true });
                                            break;

                                    }

                                }}
                                dataItemKey="id"
                                dataItems={[{ id: 1, name: 'Training' }, { id: 2, name: 'Testing' }, { id: 3, name: 'Both' }]}
                                disabled={loading}
                                infoTitle={formatMessage({ id: 'training_unlimited_time_with_detailed_fe.cbfaabb1', defaultMessage: 'Training - Unlimited time with detailed feedback Testing - Timed with no detailed feedback' })}
                                label={formatMessage({ id: 'mode.f65b1f2b', defaultMessage: 'Mode' })}
                                textField="name"
                                value={defaultSettingsForm.trainingMode && defaultSettingsForm.testMode ? { id: 3, name: 'Both' } : defaultSettingsForm.trainingMode ? { id: 1, name: 'Training' } : { id: 2, name: 'Testing' }}
                            />
                        </Col>
                    </Row>
                    <div className="d-flex">
                        <GMetrixCheckInput
                            checked={defaultSettingsForm.enableQuestionReview}
                            disabled={loading}
                            infoTitle={formatMessage({ id: 'permit_students_to_review_missed_questio.bdd3f3df', defaultMessage: 'Permit students to review missed questions without changing the score in Testing Mode.' })}
                            label={formatMessage({ id: 'question_review.8f63f046', defaultMessage: 'Question Review' })}
                            onChange={e => setDefaultSettingsForm({ ...defaultSettingsForm, enableQuestionReview: e.target.checked })}
                            type="checkbox"
                        />
                        <GMetrixCheckInput
                            checked={defaultSettingsForm.enableStudyGuide}
                            disabled={loading}
                            infoTitle={formatMessage({ id: 'permit_access_to_the_gmetrix_study_guide.ad613c7b', defaultMessage: 'Permit access to the Gmetrix Study Guides when available.' })}
                            label={formatMessage({ id: 'study_guide.8c412365', defaultMessage: 'Study Guide' })}
                            onChange={e => setDefaultSettingsForm({ ...defaultSettingsForm, enableStudyGuide: e.target.checked })}
                            type="checkbox"
                        />
                    </div>

                    <hr />

                    {/* Course Settings */}
                    <h5 className="mb-3">
                        <FormattedMessage defaultMessage="Course Settings" id="course_settings.4f8c306c" />
                    </h5>
                    <Row className="mb-3">
                        <Col md={6}>
                            <GMetrixInput
                                disabled={loading}
                                infoTitle={formatMessage({ id: 'limit_the_number_of_courses_that_a_stude.fad9a74b', defaultMessage: 'Limit the number of Courses that a student can take across all Courses in this Access Code' })}
                                label={formatMessage({ id: 'courses_per_code.139bd3bd', defaultMessage: 'Courses Per Code' })}
                                max={999}
                                onChange={e => setDefaultSettingsForm({ ...defaultSettingsForm, coursesPerCode: parseNumberValue(e.target.value, defaultSettingsForm.coursesPerCode, 999) })}
                                subLabel={`(max ${999})`}
                                type="number"
                                value={defaultSettingsForm.coursesPerCode}
                            />
                        </Col>
                        <Col md={6}>
                            <GMetrixDropdown
                                dataItemKey="Id"
                                dataItems={courseGatingTypes}
                                disabled={loading}
                                infoTitle={formatMessage({ id: 'students_must_meet_the_selected_criteria.d4d34c5c', defaultMessage: 'Students must meet the selected criteria to progress through the course' })}
                                label={formatMessage({ id: 'course_gating.10e89741', defaultMessage: 'Course Gating' })}
                                onChange={type => setDefaultSettingsForm({ ...defaultSettingsForm, courseGateType: type.Id })}
                                textField="TranslatedName"
                                value={courseGatingTypes[defaultSettingsForm.courseGateType ?? 0]}
                            >
                            </GMetrixDropdown>

                        </Col>
                    </Row>

                    <hr />

                    {/* Book Settings */}
                    <h5 className="mb-3">
                        <FormattedMessage defaultMessage="Book Settings" id="book_settings.ae00f5eb" />
                    </h5>
                    <Row className="mb-3">
                        <Col md={6}>
                            <GMetrixInput
                                disabled={loading}
                                infoTitle={formatMessage({ id: 'limit_the_number_of_books_that_a_student.20cb5ecb', defaultMessage: 'Limit the number of Books that a student can read across all Books in this Classroom' })}
                                label={formatMessage({ id: 'books_per_student.36f8ef23', defaultMessage: 'Books Per Student' })}
                                max={99}
                                onChange={e => setDefaultSettingsForm({ ...defaultSettingsForm, booksPerStudent: parseNumberValue(e.target.value, defaultSettingsForm.booksPerStudent, 99) })} subLabel={`(max ${99})`}
                                type="number"
                                value={defaultSettingsForm.booksPerStudent}
                            />
                        </Col>
                    </Row>
                </Form>
            </GMetrixDialog>

            <FindContent
                currentContext={GroupDetailsContext}
                onCancel={() => setState({ showContentModal: false })}
                onSuccess={() => setState({ showContentModal: false })}
                show={showContentModal}
                variant="dialog"
            />

        </>
    );

}
